/**
 * ProjectFolderStats Component
 * Displays high-level statistics for project folders (level 0)
 */

import React, { useEffect, useState } from 'react';
import { apiClient } from '../services/api';
import './ProjectFolderStats.css';

interface ProjectFolderStatsProps {
  folderId: string;
  folderType: 'test_scenario' | 'test_case';
}

interface Stats {
  totalSubfolders: number;
  totalItems: number;
  priorityBreakdown: {
    Critical: number;
    High: number;
    Medium: number;
    Low: number;
  };
}

export const ProjectFolderStats: React.FC<ProjectFolderStatsProps> = ({
  folderId,
  folderType
}) => {
  const [stats, setStats] = useState<Stats>({
    totalSubfolders: 0,
    totalItems: 0,
    priorityBreakdown: {
      Critical: 0,
      High: 0,
      Medium: 0,
      Low: 0
    }
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      
      // Load all folders to count subfolders and get nested structure
      const allFolders = await apiClient.getFolders(folderType);
      const directSubfolders = allFolders.filter(f => f.parent_id === folderId);
      
      // Recursively get all descendant folder IDs
      const getAllDescendantFolderIds = (parentId: string): string[] => {
        const children = allFolders.filter(f => f.parent_id === parentId);
        let allIds = children.map(f => f.id);
        
        for (const child of children) {
          allIds = [...allIds, ...getAllDescendantFolderIds(child.id)];
        }
        
        return allIds;
      };
      
      const allDescendantFolderIds = getAllDescendantFolderIds(folderId);
      
      // Load all items from all descendant folders
      let allItems: any[] = [];
      for (const subfolderId of allDescendantFolderIds) {
        try {
          if (folderType === 'test_scenario') {
            const items = await apiClient.getScenariosByFolder(subfolderId);
            allItems = [...allItems, ...items];
          } else {
            const items = await apiClient.getTestCases(subfolderId);
            allItems = [...allItems, ...items];
          }
        } catch (err) {
          // Skip folders with no items
          console.log(`No items in folder ${subfolderId}`);
        }
      }
      
      // Calculate priority breakdown
      const priorityBreakdown = {
        Critical: allItems.filter(item => item.priority === 'Critical').length,
        High: allItems.filter(item => item.priority === 'High').length,
        Medium: allItems.filter(item => item.priority === 'Medium').length,
        Low: allItems.filter(item => item.priority === 'Low').length
      };
      
      setStats({
        totalSubfolders: directSubfolders.length,
        totalItems: allItems.length,
        priorityBreakdown
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadStats();
  }, [folderId]);

  const itemLabel = folderType === 'test_scenario' ? 'Test Scenarios' : 'Test Cases';

  if (isLoading) {
    return (
      <div className="project-stats-container">
        <div className="stats-loading">Loading statistics...</div>
      </div>
    );
  }

  return (
    <div className="project-stats-container">
      <div className="stats-header">
        <h2>📊 Project Overview</h2>
        <p>High-level statistics for this project folder</p>
      </div>

      <div className="stats-grid">
        {/* Total Subfolders */}
        <div className="stat-card">
          <div className="stat-icon">📁</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalSubfolders}</div>
            <div className="stat-label">Subfolders</div>
          </div>
        </div>

        {/* Total Items */}
        <div className="stat-card">
          <div className="stat-icon">{folderType === 'test_scenario' ? '📋' : '✅'}</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalItems}</div>
            <div className="stat-label">{itemLabel}</div>
          </div>
        </div>

        {/* Total Priority Items */}
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">
              {stats.priorityBreakdown.Critical + stats.priorityBreakdown.High}
            </div>
            <div className="stat-label">High Priority</div>
          </div>
        </div>

        {/* Critical Priority */}
        <div className="stat-card priority-critical">
          <div className="stat-icon">🔴</div>
          <div className="stat-content">
            <div className="stat-value">{stats.priorityBreakdown.Critical}</div>
            <div className="stat-label">Critical</div>
          </div>
        </div>

        {/* High Priority */}
        <div className="stat-card priority-high">
          <div className="stat-icon">🟠</div>
          <div className="stat-content">
            <div className="stat-value">{stats.priorityBreakdown.High}</div>
            <div className="stat-label">High</div>
          </div>
        </div>

        {/* Medium Priority */}
        <div className="stat-card priority-medium">
          <div className="stat-icon">🟡</div>
          <div className="stat-content">
            <div className="stat-value">{stats.priorityBreakdown.Medium}</div>
            <div className="stat-label">Medium</div>
          </div>
        </div>

        {/* Low Priority */}
        <div className="stat-card priority-low">
          <div className="stat-icon">🟢</div>
          <div className="stat-content">
            <div className="stat-value">{stats.priorityBreakdown.Low}</div>
            <div className="stat-label">Low</div>
          </div>
        </div>
      </div>

      <div className="stats-footer">
        <div className="stats-message">
          <span className="info-icon">💡</span>
          <span>Create subfolders to organize your {itemLabel.toLowerCase()} and start building your test suite</span>
        </div>
      </div>
    </div>
  );
};
